/**
 * Wealth-signals service — monitored events (liquidity, investment,
 * philanthropy, people moves, risk) and the people / organizations linked to
 * them. Mirrors the split in research.js: this file owns the fetch calls,
 * adapters.js owns the raw-API-shape-to-UI-shape transforms.
 *
 * Unlike the research/search endpoints, these responses are already
 * display-formatted by the backend (dates like "6 Oct 2026", values like
 * "$100M", "—" for missing), so the adapters only normalise pagination and
 * guard against missing arrays.
 */
import { apiFetch } from './client.js';
import { signalEventsPageFromApi, signalPeoplePageFromApi } from './adapters.js';

const BASE = '/wealth-events';

/**
 * Filter vocabularies: { categories, event_types_by_category, countries,
 * industries }. Shared by the events and people lists.
 */
export async function getSignalFilters({ signal } = {}) {
  const payload = await apiFetch(`${BASE}/meta/filters`, { signal });
  return {
    categories: payload?.categories ?? [],
    eventTypesByCategory: payload?.event_types_by_category ?? {},
    countries: payload?.countries ?? [],
    industries: payload?.industries ?? [],
  };
}

export async function listSignalEvents({ category, eventType, country, q, page = 1, size = 50, signal } = {}) {
  const payload = await apiFetch(`${BASE}/events`, {
    params: { category, event_type: eventType, country, q, page, page_size: size },
    signal,
  });
  return signalEventsPageFromApi(payload);
}

export async function getSignalEvent(id, { signal } = {}) {
  const payload = await apiFetch(`${BASE}/events/${encodeURIComponent(id)}`, { signal });
  return payload?.ev ?? payload;
}

export async function listSignalPeople({ industry, country, q, page = 1, size = 50, signal } = {}) {
  const payload = await apiFetch(`${BASE}/people`, {
    params: { industry, country, q, page, page_size: size },
    signal,
  });
  return signalPeoplePageFromApi(payload);
}

/** Person or organization profile — `kind` is 'person' | 'org'. */
export async function getSignalEntity(kind, id, { signal } = {}) {
  const base = `${BASE}/${kind === 'org' ? 'organizations' : 'people'}`;
  const payload = await apiFetch(`${base}/${encodeURIComponent(id)}`, { signal });
  return payload?.prof ?? payload;
}
